import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import type { SendProps } from 'send/Send'
import { MonolithWallet } from '@node-space/types'
import { ContactSelector } from 'components/Contacts/ContactSelector'
import { Approval, PathNames } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useWalletQuery } from 'hooks/queries/useWalletQuery'
import { useWalletsQueryV2 } from 'hooks/queries/useWalletsQueryV2'
import { useGetWalletLegacyId } from 'hooks/queries/Wallets/useGetWalletLegacyId'
import { useApprovalsConfigForFlow } from 'hooks/useApprovalsConfigForFlow'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useSupportedCurrencies } from 'hooks/useSupportedCurrencies'
import { useWalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ActionIds } from 'types/types'
import { createMFELazy } from 'utils/createMFELazy'
import track from 'utils/tracker'

const SendLazy = createMFELazy<SendProps>(() => import('send/Send'), {
  mfeName: 'Send',
  testId: 'send-mfe-error',
})

export const SendMFE = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { profile } = useProfileContext()
  const { enableCryptoFiatSend } = useFeatureFlags()

  const context = useWalletActionContext()
  const walletAction = context?.[0] ?? null
  const setWalletAction = context?.[1]
  const sourceWallet = walletAction?.meta?.sourceWallet as MonolithWallet | undefined
  const customerWalletLsid = walletAction?.meta?.customerWallet?.id

  const { data: customerWalletLegacyId } = useGetWalletLegacyId(
    !sourceWallet && customerWalletLsid ? customerWalletLsid : ''
  )
  const { data: resolvedSourceWallet } = useWalletQuery(
    customerWalletLegacyId ?? 0,
    !!customerWalletLegacyId
  )
  const effectiveSourceWallet: MonolithWallet | undefined = sourceWallet ?? resolvedSourceWallet

  const { accountCapabilities } = useAccountsContext()
  const isEpcEnabled = accountCapabilities?.isEpcWalletPayoutsCapabilityEnabled
  const { getSupportedCurrencyWallets } = useSupportedCurrencies()

  const [walletSearchTerm, setWalletSearchTerm] = useState('')
  const [userSelectedWallet, setUserSelectedWallet] = useState<MonolithWallet | undefined>()
  const selectedWallet = userSelectedWallet ?? effectiveSourceWallet

  const [fiatPayoutInfo, setFiatPayoutInfo] = useState<{
    amount: string
    currency: string
    isFirstParty: boolean
  } | null>(null)

  const { data: walletsData, isFetching: isFetchingWallets } = useWalletsQueryV2({
    walletType: '',
    searchTerm: walletSearchTerm,
    max: 100,
    order: 'asc',
    sort: 'description',
    includeEmbedded: isEpcEnabled,
  })

  const handleWalletSearch = useCallback((searchTerm: string) => {
    setWalletSearchTerm(searchTerm)
  }, [])

  // crypto payout approvals configuration
  const { approvalsIsRequiredForFlow: cryptoApprovalsRequired } = useApprovalsConfigForFlow(
    Approval.FLOWS.CRYPTO_PAYOUT
  )

  // fiat payout approvals configuration
  const fiatApprovalType = fiatPayoutInfo?.isFirstParty
    ? Approval.FLOWS.FIAT_1ST_PARTY_PAYOUT
    : Approval.FLOWS.FIAT_3RD_PARTY_PAYOUT

  const fiatAmountSummary = fiatPayoutInfo
    ? { amount: fiatPayoutInfo.amount, currency: fiatPayoutInfo.currency }
    : undefined

  const { approvalsIsRequiredForFlow: fiatApprovalsRequired } = useApprovalsConfigForFlow(
    fiatApprovalType,
    fiatAmountSummary
  )

  const handleCloseOrComplete = useCallback(() => {
    walletAction?.onClose?.()
  }, [walletAction])

  const handleHeaderChange = useCallback(
    (header: string) => setWalletAction?.(prev => (prev ? { ...prev, header } : null)),
    [setWalletAction]
  )

  const handleBackChange = useCallback(
    (onBack: (() => void) | undefined) =>
      setWalletAction?.(prev => (prev ? { ...prev, onBack } : null)),
    [setWalletAction]
  )

  const handleNavigateToPayments = useCallback(
    () => navigate(`${PathNames.PAYMENTS}${PathNames.CRYPTO}`),
    [navigate]
  )

  const handleOpenConvert = () => {
    setWalletAction?.({
      actionId: ActionIds.CONVERT,
      header: t('wallets.convert.detailsStepHeader'),
      onClose: () => {
        walletAction?.onClose?.()
      },
    })
  }

  const filteredWallets = useMemo(
    () => getSupportedCurrencyWallets(walletsData?.data ?? []) as MonolithWallet[],
    [walletsData?.data, getSupportedCurrencyWallets]
  )
  const wallets = useMemo(
    () =>
      effectiveSourceWallet && !filteredWallets.some(w => w.lsid === effectiveSourceWallet.lsid)
        ? [effectiveSourceWallet, ...filteredWallets]
        : filteredWallets,
    [effectiveSourceWallet, filteredWallets]
  )

  const featureFlags = useMemo(
    () => ({
      fiatTwoFactorEnabled: profile?.twoFactorEnabled || false,
      enableCryptoFiatSend: enableCryptoFiatSend,
    }),
    [profile?.twoFactorEnabled, enableCryptoFiatSend]
  )

  const onWalletChange = (wallet: MonolithWallet | undefined) => {
    setUserSelectedWallet(wallet)
  }

  const ContactSelectorWithWallet = useCallback(
    (props: React.ComponentProps<typeof ContactSelector>) => (
      <ContactSelector {...props} wallet={selectedWallet} onClose={handleCloseOrComplete} />
    ),
    [selectedWallet?.lsid, handleCloseOrComplete]
  )

  const sendProps: SendProps = {
    wallets,
    sourceWallet: effectiveSourceWallet,
    approvalsRequired: cryptoApprovalsRequired ?? false,
    isLoadingWallets: isFetchingWallets,
    onOpenConvert: handleOpenConvert,
    onHeaderChange: handleHeaderChange,
    onBackChange: handleBackChange,
    onClose: handleCloseOrComplete,
    onComplete: handleCloseOrComplete,
    onWalletSearch: handleWalletSearch,
    onNavigateToPayments: handleNavigateToPayments,
    onPayoutSuccess: walletAction?.meta?.onPayoutSuccess,
    onWalletChange,
    ContactSelector: ContactSelectorWithWallet,
    bffUrl: process.env.VITE_BASE_URL,
    featureFlags,
    appTag: 'merchant-portal',
    onTrack: track.Amp.track,
    // fiat
    fiatApprovalsRequired: fiatApprovalsRequired ?? false,
    onAmountChangeForFiatApprovalCheck: (amount, currency, isFirstParty) =>
      setFiatPayoutInfo({ amount, currency, isFirstParty }),
  }

  return <SendLazy {...sendProps} />
}
